/**
 * WalletOutcomeEvidence — printable HTML view of the Consumer Outcome
 * Evidence. This is NOT a certificate of acceptance or contracting; it
 * is Altara's hash-chained record of the consumer's decision journey.
 * Fetches `/wallet/decisions/{id}/outcome-evidence`.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Download, Shield } from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  fetchOutcomeEvidence, zar, MORE_INFO_TOPICS, DO_NOT_PROCEED_REASONS, CONSUMER_RESPONSE,
} from "@/lib/wallet-decisions";
import { loadWallet } from "@/lib/wallet-storage";

export default function WalletOutcomeEvidence() {
  const { id } = useParams();
  const nav = useNavigate();
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;

  const [oe, setOE] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!deviceId) { nav("/wallet/onboarding", { replace: true }); return; }
    let c = false;
    (async () => {
      try {
        const data = await fetchOutcomeEvidence(id, deviceId);
        if (!c) setOE(data);
      } catch (e) { if (!c) setErr(e?.response?.data?.detail || "Could not load evidence."); }
    })();
    return () => { c = true; };
  }, [id, deviceId, nav]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="outcome-evidence-error">
          <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>Evidence unavailable</div>
          <div className="text-[13px] text-slate-500 mt-1">{err}</div>
          <Link to={`/wallet/decisions/${id}`} className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium" style={{ color: NAVY }}>
            ← Back to decision
          </Link>
        </div>
      </div>
    );
  }
  if (!oe) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400" data-testid="outcome-evidence-loading">Loading evidence…</div>;

  const d = oe.decision;
  const cp = oe.counterparty;
  const j = oe.journey;
  const response = j.consumer_response || {};
  const isMoreInfo = response.consumer_response === CONSUMER_RESPONSE.MORE_INFO;
  const respLabel = response.consumer_response === CONSUMER_RESPONSE.READY ? "Ready to proceed"
                    : response.consumer_response === CONSUMER_RESPONSE.DO_NOT ? "Don't wish to proceed"
                    : "More information requested";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="outcome-evidence-page">
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between print:hidden">
        <Link to={`/wallet/decisions/${id}`} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900" data-testid="outcome-evidence-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to decision room
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            data-testid="outcome-evidence-print"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/wallet/decisions/${id}/outcome-evidence?device_id=${encodeURIComponent(deviceId)}`}
            target="_blank" rel="noreferrer"
            data-testid="outcome-evidence-json"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold border border-slate-200 bg-white"
            style={{ color: NAVY }}
          >
            <Download className="w-3.5 h-3.5" /> JSON
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 lg:p-8 print:p-0">
        <article className="bg-white shadow-lg print:shadow-none border border-slate-100 rounded-2xl print:rounded-none">
          <div className="rounded-t-2xl print:rounded-none px-8 py-6 flex items-center justify-between" style={{ background: NAVY, color: "white" }}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>Altara Consumer Intelligence</div>
              <div className="mt-1 font-display text-2xl font-semibold">Consumer Outcome Evidence</div>
              <div className="text-[11px] mt-1 opacity-80">{oe.specification} · issued {new Date(oe.issued_at).toLocaleString()}</div>
            </div>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-display font-bold text-2xl" style={{ background: GOLD, color: NAVY }}>A</div>
          </div>

          <div className="px-8 py-6 space-y-6">
            <div className="rounded-xl p-3 flex items-start gap-2 text-[11.5px]" style={{ background: "#F1F5F9" }}>
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
              <div className="text-slate-600 leading-relaxed">
                {oe.boundary_notice || "Altara records the consumer journey and intended next step. Nothing has been accepted, signed, contracted or paid through Altara."}
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Evidence ID</div>
                <div className="font-mono text-[13px] mt-0.5" style={{ color: NAVY }}>{oe.outcome_evidence_id}</div>
              </div>
              <div className="text-right">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Consumer response</div>
                <div className="text-[13px] font-semibold mt-0.5" style={{ color: NAVY }}>{respLabel}</div>
              </div>
            </div>

            <Section title="Decision">
              <TwoCol rows={[
                ["Proposal name", d.proposal_name],
                ["Proposal ID / version", `${d.proposal_document_id} · ${d.proposal_version}`],
                ["Type", d.decision_type],
                ["Financial commitment", zar(d.financial_commitment_zar)],
                ["Investment term", `${d.investment_term_months} months`],
                ["Delivered via", (d.source || "").replace(/_/g, " ")],
                ["Delivered at", new Date(d.sent_at).toLocaleString()],
              ]} />
            </Section>

            <Section title="Counterparty">
              <TwoCol rows={[
                ["Institution", cp.institution_name],
                ["Registration", cp.institution_registration],
                ["KY-I status", cp.institution_kyc_verified ? "Verified" : "Not verified"],
                ["Verified via", cp.institution_verified_via],
                ["Adviser", cp.adviser_name],
                ["Adviser role", cp.adviser_role],
                ["Adviser reg. number", cp.adviser_reg_number],
              ]} />
            </Section>

            <Section title="Consumer response · intended next step">
              <TwoCol rows={[
                ["Response", respLabel],
                ["Recorded at", response.recorded_at ? new Date(response.recorded_at).toLocaleString() : ""],
                ...(isMoreInfo && response.more_info_topics
                  ? [["More info about", response.more_info_topics.map(id => (MORE_INFO_TOPICS.find(t => t.id === id)?.label || id)).join(", ")]] : []),
                ...(response.response_reason && !Array.isArray(response.response_reason)
                  ? [["Reason", DO_NOT_PROCEED_REASONS.find(r => r.id === response.response_reason)?.label || response.response_reason]] : []),
                ...(response.more_info_note ? [["Free-text note", response.more_info_note]] : []),
              ]} />
            </Section>

            <Section title="Journey">
              <JourneyRow stage="Trust" when={j.trust_ack?.acknowledged_at} body="Proposal provenance, adviser identity and institution KY-I confirmed by the consumer." />
              <JourneyRow stage="Understand" when={j.understand_attest?.acknowledged_at} body={`${(j.understand_attest?.attested_ids || []).length} attestations recorded. Saved questions: ${(j.understand_attest?.saved_questions || []).length}.`} />
              <JourneyRow stage="Check · Consumer Context" when={j.check_review?.reviewed_at} body={j.check_review?.context?.message} />
              <JourneyRow stage={`Act · ${respLabel}`} when={response.recorded_at} body={isMoreInfo
                ? `Consumer asked for more information about: ${(response.more_info_topics || []).map(id => (MORE_INFO_TOPICS.find(t => t.id === id)?.label || id)).join(", ")}`
                : (response.response_reason ? `Reason: ${(DO_NOT_PROCEED_REASONS.find(r => r.id === response.response_reason)?.label || response.response_reason)}` : "Response recorded.")} />
            </Section>

            <Section title="Evidence trail (immutable)">
              <ol className="space-y-1.5">
                {oe.evidence_trail.map(e => (
                  <li key={e.event_id} className="text-[11.5px] flex items-baseline gap-2">
                    <span className="font-mono text-slate-400 flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
                    <span className="font-semibold" style={{ color: NAVY }}>{e.stage}</span>
                    <span className="text-slate-500">· {e.event_type.replace(/_/g, " ").toLowerCase()}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-4 rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Evidence hash chain (SHA-256)</div>
                <div className="font-mono text-[10.5px] break-all mt-1" style={{ color: NAVY }}>{oe.evidence_hash_chain}</div>
              </div>
            </Section>

            <div className="border-t border-slate-100 pt-4 text-[10.5px] text-slate-500 leading-relaxed">
              This record is a portable, machine-verifiable account of the consumer's decision journey and intended next step. It is NOT a certificate of acceptance, contracting or fulfilment — those remain the institution's responsibility through its own channels. Verify authenticity by re-computing the hash chain over the evidence trail.
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="print:break-inside-avoid">
      <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-2" style={{ color: GOLD }}>{title}</div>
      {children}
    </section>
  );
}
function TwoCol({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
      {rows.filter(r => r[1] !== undefined && r[1] !== null && r[1] !== "").map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2">
          <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 flex-shrink-0 w-32">{k}</dt>
          <dd className="text-[12.5px] truncate" style={{ color: NAVY }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
function JourneyRow({ stage, when, body }) {
  if (!when) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: GOLD }} />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{stage}</div>
        <div className="text-[10.5px] text-slate-400">{new Date(when).toLocaleString()}</div>
        {body && <div className="text-[11.5px] text-slate-600 mt-1 leading-relaxed">{body}</div>}
      </div>
    </div>
  );
}
